import React from "react";
import { StyleSheet, Text, View } from "react-native";
import {
  BookOpenIcon,
  BriefcaseIcon,
  LightbulbIcon,
  ShieldIcon,
  WheelchairIcon,
  type Icon as PhosphorIcon,
} from "@phosphor-icons/react";
import { ModernizedRecommendationSourceType } from "@axio/graphql/generated/graphql";

import { colors } from "../../../../../styles/design-system";
import type { ActivityRecommendationSource } from "../types/reviewTypes";

type RecommendationSourceMeta = {
  Icon: PhosphorIcon;
  color: string;
  fallbackLabel: string;
};

const SOURCE_META: Record<ModernizedRecommendationSourceType, RecommendationSourceMeta> = {
  [ModernizedRecommendationSourceType.Rubric]: {
    Icon: ShieldIcon,
    color: colors.brand[600],
    fallbackLabel: "Rubric standard",
  },
  [ModernizedRecommendationSourceType.Industry]: {
    Icon: BriefcaseIcon,
    color: colors.warning[700],
    fallbackLabel: "Industry alignment",
  },
  [ModernizedRecommendationSourceType.Accessibility]: {
    Icon: WheelchairIcon,
    color: colors.success[700],
    fallbackLabel: "Accessibility standard",
  },
  [ModernizedRecommendationSourceType.Institutional]: {
    Icon: BookOpenIcon,
    color: colors.accent.purple600,
    fallbackLabel: "Institutional guideline",
  },
  [ModernizedRecommendationSourceType.General]: {
    Icon: LightbulbIcon,
    color: colors.grey[600],
    fallbackLabel: "General recommendation",
  },
};

function getSourceMeta(sourceType: ActivityRecommendationSource["sourceType"]): RecommendationSourceMeta {
  return (
    SOURCE_META[sourceType ?? ModernizedRecommendationSourceType.General] ??
    SOURCE_META[ModernizedRecommendationSourceType.General]
  );
}

interface RecommendationSourceListProps {
  recommendations?: ActivityRecommendationSource[];
}

function RecommendationSourceList({ recommendations }: RecommendationSourceListProps) {
  const visibleRecommendations =
    recommendations?.filter((recommendation) => recommendation.recommendation || recommendation.rationale) ?? [];

  if (visibleRecommendations.length === 0) {
    return null;
  }

  return (
    <View style={styles.list}>
      {visibleRecommendations.map((recommendation) => {
        const sourceMeta = getSourceMeta(recommendation.sourceType);
        const Icon = sourceMeta.Icon;
        const recommendationText = recommendation.recommendation ?? sourceMeta.fallbackLabel;

        return (
          <View key={recommendation.id} style={styles.row}>
            <View style={styles.iconSlot}>
              <Icon size={13} color={sourceMeta.color} weight="regular" />
            </View>
            <Text style={styles.textWrap}>
              <Text style={[styles.recommendationText, { color: sourceMeta.color }]}>{recommendationText}</Text>
              {recommendation.rationale && (
                <Text style={styles.rationaleText}>{` \u2014 ${recommendation.rationale}`}</Text>
              )}
            </Text>
          </View>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  list: {
    gap: 8,
    marginTop: 4,
  },
  row: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
  },
  iconSlot: {
    width: 16,
    height: 18,
    alignItems: "center",
    justifyContent: "center",
  },
  textWrap: {
    flex: 1,
    flexShrink: 1,
    minWidth: 0,
    fontFamily: "Inter",
    fontSize: 12,
    lineHeight: 18,
  },
  recommendationText: {
    fontFamily: "Inter",
    fontSize: 12,
    fontWeight: "500",
    lineHeight: 18,
  },
  rationaleText: {
    color: colors.grey[600],
    fontFamily: "Inter",
    fontSize: 12,
    fontWeight: "500",
    lineHeight: 18,
  },
});

export default RecommendationSourceList;
